import { useMemo } from "react";

import type { ApiError } from "./client";
import { useResource } from "./useResource";
import { useRealtimeScope } from "../realtime/useRealtimeScope";

// GET /api/v1/tasks/:id/build-statuses → { build_statuses: [...] }.
export type ApiBuildStatus = {
  readonly commit_sha: string | null;
  readonly completed_at: string | null;
  readonly conclusion: string | null;
  readonly context: string;
  readonly details_url: string | null;
  readonly kind: string;
  readonly last_seen_at: string | null;
  readonly public_id: string;
  readonly started_at: string | null;
  readonly state: string;
  readonly summary: string | null;
  readonly title: string | null;
};

export type ChecksSummary = "failing" | "none" | "passing" | "pending";

// The server computes this authoritatively (specifications/build-status.md
// §Aggregate checks summary); the client renders it rather than re-deriving it.
export type ChecksSummaryDetail = {
  readonly failing: number;
  readonly head_sha: string | null;
  readonly passing: number;
  readonly pending: number;
  readonly required_total: number;
  readonly state: ChecksSummary;
};

// What a task with no answer yet reads as — before the first response lands, or
// after one that failed. Not a client-side rollup: the summary is the server's
// to compute (`build_status/checksSummary.ts`), and the second implementation
// that used to stand in here classified on `conclusion`, which is the very bug
// finding #18 took out of the server. One classifier, on one side.
export const NO_CHECKS: ChecksSummaryDetail = {
  failing: 0,
  head_sha: null,
  passing: 0,
  pending: 0,
  required_total: 0,
  state: "none",
};

export type UseTaskBuildStatuses = {
  readonly detail: ChecksSummaryDetail;
  readonly error: ApiError | undefined;
  readonly loading: boolean;
  readonly refetch: () => void;
  readonly statuses: ReadonlyArray<ApiBuildStatus>;
  readonly summary: ChecksSummary;
};

export const useTaskBuildStatuses = (
  taskId: string | null,
): UseTaskBuildStatuses => {
  const { data, error, loading, refetch } = useResource<{
    build_statuses: ReadonlyArray<ApiBuildStatus>;
    checks_summary: ChecksSummaryDetail;
  }>(taskId ? `/tasks/${taskId}/build-statuses` : null);
  // Statuses change on build events, and the whole summary re-keys when the task
  // head moves (a new diff snapshot, or a push to the pull request), so refetch
  // on all of them. `build_status.rollup` is the aggregate moving on its own —
  // a back-resolve materialising checks that arrived before their commit did,
  // which no per-row event announces.
  useRealtimeScope(taskId ? `task:${taskId}` : null, (event) => {
    if (
      event.kind === "build_status.updated" ||
      event.kind === "build_status.terminal" ||
      event.kind === "build_status.rollup" ||
      event.kind === "diff.snapshot.created" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });
  const statuses = useMemo(() => data?.build_statuses ?? [], [data]);
  const detail = data?.checks_summary ?? NO_CHECKS;
  return { detail, error, loading, refetch, statuses, summary: detail.state };
};
